import { createFileRoute, redirect, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { rendererClient } from "../../lib/rendererClient";
import { getRequestHeader } from "@tanstack/react-start/server";
import { Renderer } from "../../components/Renderer";
import type {
  Data,
  Excursion,
  ContentRootProps,
  ContentComponentConfig,
} from "@op/puck-types";
import { RenderProvider } from "@op/utils";
import { publicEnv } from "../../utils/env";

const paramsValidator = z.object({ versionId: z.string() });
const searchValidator = z.object({
  key: z.string().optional(),
});

const getPreviewPageContent = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      params: paramsValidator,
      search: searchValidator,
    }),
  )
  .handler(
    async ({
      data: {
        params: { versionId },
        search: { key },
      },
    }) => {
      const cookieHeader = getRequestHeader("Cookie");
      if (!cookieHeader && !key) throw redirect({ to: "/" });

      const res = await rendererClient.preview[":versionId"].$get({
        param: {
          versionId,
        },
        header: {
          Cookie: cookieHeader,
          "X-API-Key": key,
        },
      });
      if (!res.ok) {
        if (res.status === 404) throw notFound();
        throw new Error(
          `Failed to get preview page content: ${res.statusText}`,
        );
      }

      const data = await res.json();

      await Bun.write(
        "./citations.json",
        JSON.stringify(data.citations, null, 2),
      );

      return { ...data };
    },
  );

export const Route = createFileRoute("/preview/$versionId")({
  component: RouteComponent,
  validateSearch: searchValidator,
  loaderDeps: ({ search }) => ({
    search,
  }),
  loader: async ({
    params,
    deps: {
      search: { key },
    },
  }) => {
    try {
      const previewData = await getPreviewPageContent({
        data: { params, search: { key } },
      });
      if (!previewData) throw new Error("No preview data");
      return previewData;
    } catch {
      throw redirect({ to: "/" });
    }
  },
});

function RouteComponent() {
  const {
    content,
    excursions,
    citations,
    glossaryItems,
    versions,
    meta,
    outline,
    figures,
  } = Route.useLoaderData();
  const { renderMedium } = Route.useRouteContext();

  return (
    <RenderProvider
      value={{
        context: "renderer",
        renderMedium: renderMedium as "print" | "screen",
        apiUrl: publicEnv.VITE_API_URL,
      }}
    >
      <Renderer
        content={content as Data<ContentComponentConfig, ContentRootProps>}
        excursions={excursions as Excursion[]}
        citations={citations}
        glossaryItems={glossaryItems}
        versions={versions}
        meta={{
          ...meta,
          publishedAt: meta.publishedAt ? new Date(meta.publishedAt) : null,
        }}
        outline={outline}
        figures={figures}
      />
    </RenderProvider>
  );
}
